/* The safety net under the hand-written data-private tags.
 *
 * The tags are exact but they rely on somebody remembering. Add a field called `iban` in six
 * months, forget the tag, and nothing looks broken — it just quietly stops being covered, and
 * nobody finds out until it is on a recorded call. So every field is also read by name here,
 * and anything that looks personal is tagged for you.
 *
 * It errs towards hiding. The two mistakes are not the same size: a field that blurs when it
 * needn't is a small annoyance with the off switch right there on screen, and a field that does
 * not blur when it should is somebody's account number in a recording.
 *
 * This does not replace the explicit tags. A name is a guess; a tag is a decision. Some fields
 * here are named nothing useful at all — the IBAN box on the banking form is called nothing and
 * its placeholder is an example number — and only the tag catches those. */

const SENSITIVE = [
    /\biban\b/,
    /\b(swift|bic)\b/,
    /\ba ?c (no|number|name|holder)\b/,
    /\baccount (no|number|holder)\b/,
    /\bsort code\b/,
    /\bbank address\b/,
    /\b(mailing|postal|home|residential|street|billing) address\b/,
    /\baddress\b/,
    /\b(phone|mobile|telephone|whatsapp)\b/,
    /\bpassport\b/,
    /\baadhaar\b/,
    /\b(id|identity|identification|national id) (no|number)\b/,
    /\b(date of birth|dob|birth date)\b/,
    /\b(tax|vat) (id|no|number)\b/,
    /\bssn\b/,
];

/* Things that read as sensitive but are not. "Email Address" is the one that matters: it
 * contains "address", and blurring the email people sign in with would break the demo this
 * whole feature exists to protect. */
const NOT_SENSITIVE = [
    /\bemail\b/,
    /\bbank name\b/,
    /\bip address\b/,
    /\b(url|website|web site|linkedin)\b/,
];

const MARK = 'data-private-checked';

// Names in this codebase are snake_case, kebab-case or human placeholders. Flattening all three
// to plain words means one set of patterns covers the lot.
function normalise(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/* Everything the field says about itself, plus its label.
 *
 * The label is worth the trouble because this codebase mostly writes
 * `<div><label>IBAN</label><input/></div>` with no htmlFor to link them, so the only name the
 * field has is sitting in a sibling. Two levels up at most, and only where that ancestor holds
 * this one field — otherwise a section heading like "Banking details" would speak for every
 * input beneath it and blur the whole block. */
function describe(el) {
    const bits = [el.name, el.id, el.placeholder, el.getAttribute('aria-label')];

    let node = el.parentElement;
    for (let up = 0; up < 2 && node; up += 1, node = node.parentElement) {
        if (node.querySelectorAll('input, textarea, select').length !== 1) break;
        const label = node.querySelector('label');
        if (label) {
            bits.push(label.textContent);
            break;
        }
    }

    return normalise(bits.join(' '));
}

function looksPersonal(words) {
    if (!words) return false;
    if (NOT_SENSITIVE.some((re) => re.test(words))) return false;
    return SENSITIVE.some((re) => re.test(words));
}

// A password field already shows dots, which give the length without the characters — exactly
// what is wanted, for free. The rest carry no text to read.
const SKIP_TYPES = new Set(['password', 'checkbox', 'radio', 'file', 'submit', 'button', 'reset', 'hidden', 'range', 'color']);

function consider(el) {
    if (el.hasAttribute(MARK)) return;
    el.setAttribute(MARK, '');

    if (el.tagName === 'INPUT' && SKIP_TYPES.has(el.type)) return;
    if (el.hasAttribute('data-private')) return;
    if (el.hasAttribute('data-private-never')) return;

    const words = describe(el);
    if (!looksPersonal(words)) return;

    el.setAttribute('data-private', '');

    if (import.meta.env.DEV) {
        // Says it once, where whoever added the field will see it, the day they add it.
        console.warn(
            `[privacy] Hid a field nobody tagged: "${words}". Add data-private to it, or ` +
            `data-private-never if it is not personal.`,
            el,
        );
    }
}

function scan(root) {
    if (root.nodeType !== 1) return;
    if (root.matches?.('input, textarea')) consider(root);
    root.querySelectorAll?.('input, textarea').forEach(consider);
}

/* MutationObserver rather than a pass per route change, because a field can appear at any
 * moment — the banking block unfolds when "Edit" is pressed, the email form when "Change" is.
 * Its callback runs before the browser paints, so a field is never briefly readable. */
export function startAutoPrivate() {
    scan(document.body);

    const observer = new MutationObserver((records) => {
        for (const record of records) {
            record.addedNodes.forEach(scan);
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
}
